<template>
    <div class="container">
        <div class="menu-items">
            <div v-for="menuItem in visibleMenuItems"
                 :id="menuItem.id"
                 :key="menuItem.id"
                 @click="menuItem.action"
                 :class="['menu-item', { 'active': isCurrentRoute(menuItem.routeName) } ]">
                {{ menuItem.label }}
            </div>
        </div>
        <a id="admin-site"
           class="link-item"
           :href="wwsUrl"
           target="_blank"
           rel="noopener noreferrer">
            <span>Admin</span>
            <span class="material-symbols-outlined">open_in_new</span>
        </a>
    </div>
</template>

<script lang="ts">
    import { defineComponent } from 'vue';
    import { wwsUrlService } from '@/services/wwsUrlService';
    import { navigationService } from '@/services/navigationService';

    import { useUserStore } from '@/stores/userStore';

    interface MenuItem {
        id: string;
        label: string;
        routeName: string;
        action: () => void;
        showCondition?: boolean;
    }

    export default defineComponent({
        name: "MenuBar",

        data() {
            return {
                isGroupAdmin: false,
                menuItems: [
                    {
                        id: 'go-home',
                        label: 'Document Translator',
                        routeName: navigationService.homePageName,
                        action: this.goToHomePage
                    },
                    {
                        id: 'user-info',
                        label: this.$t('menu.userInfo'),
                        routeName: navigationService.userInfoPageName,
                        action: this.goToUserInfoPage
                    },
                    {
                        id: 'subscription-info',
                        label: this.$t('menu.subscription'),
                        routeName: navigationService.subscriptionInfoPageName,
                        action: this.goSubscriptionInfoPage,
                        showCondition: true
                    },
                ] as MenuItem[],
                userStore: useUserStore()
            };
        },
        computed: {
            wwsUrl(): string {
                return wwsUrlService.homePage();
            },
            currentRouteName(): string {
                return this.$route.name as string;
            },
            visibleMenuItems(): MenuItem[] {
                return this.menuItems.filter(item => {
                    // If item requires group admin, only show if user is group admin
                    if (item.showCondition) {
                        return this.isGroupAdmin;
                    }
                    // Otherwise, show the item
                    return true;
                });
            }
        },
        async mounted() {
            // Check if user is group admin
            try {
                var groupAdmin = this.userStore.isGroupAdmin;
                var managedByStripe = this.userStore.subscriptionManagedByStripe;

                this.isGroupAdmin = groupAdmin && managedByStripe;
            } catch (error) {
                console.error('Error checking group admin status:', error);
                this.isGroupAdmin = false;
            }
        },
        methods: {
            goToHomePage(): void {
                navigationService.goHomePage();
            },
            goToUserInfoPage(): void {
                navigationService.goUserInfoPage();
            },
            goSubscriptionInfoPage(): void {
                navigationService.goSubscriptionInfoPag();
            },
            isCurrentRoute(route: string): boolean {
                return this.currentRouteName === route;
            },
            doNothing(): void {
            },
        }
    });
</script>

<style lang="scss" scoped>
    .container {
        color: var(--text-color);
        font-weight: 500;
        margin: 0 40px;
        display: flex;
        justify-content: space-between;
        width: 100%;

        .menu-items {
            display: flex;

            .menu-item {
                margin-right: 20px;
                cursor: pointer;
                border-bottom: 2px solid transparent;

                &:hover {
                    border-bottom: 2px solid;
                }

                &.active {
                    color: var(--yellow, #3498db);
                    font-weight: 600;
                    border-bottom: 2px solid;
                }
            }
        }

        .link-item {
            cursor: pointer;
            text-decoration: none;
            color: var(--text-color);
            display: flex;
            align-items: center;

            & > span {
                margin-left: 5px;
            }
        }
    }
</style>